/**
 * Agenda: selecionar paciente → confirmar → ir direto ao atendimento.
 * Reusa ProntoAtendimentoPatientStep; sem passo de pagamento.
 */
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  ProntoAtendimentoPatientStep,
  type ProntoSelectedPatient,
} from "@/components/schedule/prontoAtendimento/ProntoAtendimentoPatientStep";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import { usePatientEvolution } from "./usePatientEvolution";
import {
  TeleconsultoriaProfessionalPicker,
  type TeleconsultoriaGuest,
} from "src-next/modules/teleconsultoria";

interface EvolutionFromScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  schedules?: fhir5.Schedule[];
  onSuccess?: () => void;
}

type Step = "patient" | "teleconsultoria" | "confirm";

export function EvolutionFromScheduleDialog({
  open,
  onClose,
  schedules,
  onSuccess,
}: EvolutionFromScheduleDialogProps) {
  const [step, setStep] = useState<Step>("patient");
  const [patient, setPatient] = useState<ProntoSelectedPatient | null>(null);
  const [guest, setGuest] = useState<TeleconsultoriaGuest | null>(null);
  const { contractTeleconsultoria } = useTeleconsultoriaContractContext();
  const { loading, startOrContinueEvolution, professionalDisplay } =
    usePatientEvolution({ schedules });

  useEffect(() => {
    if (!open) return;
    setStep("patient");
    setPatient(null);
    setGuest(null);
  }, [open]);

  const handlePatientContinue = (selected: ProntoSelectedPatient) => {
    setPatient(selected);
    setGuest(null);
    // Sem a extensão da organização, o host não convida teleconsultor.
    setStep(contractTeleconsultoria ? "teleconsultoria" : "confirm");
  };

  const handleConfirm = async () => {
    if (!patient) return;
    const ok = await startOrContinueEvolution(
      {
        reference: patient.reference,
        display: patient.display,
        cpf: patient.cpf,
        responsibleCpf: patient.responsibleCpf,
      },
      guest,
      // Agenda sempre cria uma evolução nova; continuar fica no prontuário.
      { forceNew: true },
    );
    if (ok) {
      onSuccess?.();
      onClose();
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === "patient"
          ? "Evolução — selecionar paciente"
          : step === "teleconsultoria"
            ? "Teleconsultoria (opcional)"
            : "Iniciar evolução"}
      </DialogTitle>
      <DialogContent dividers>
        {step === "patient" ? (
          <ProntoAtendimentoPatientStep onContinue={handlePatientContinue} />
        ) : step === "teleconsultoria" ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selecione um teleconsultor ou prossiga sem selecionar.
            </Typography>
            <TeleconsultoriaProfessionalPicker
              selected={guest}
              onSelect={setGuest}
            />
          </>
        ) : (
          <>
            <Typography variant="body1" fontWeight={600}>
              Paciente: {patient?.display}
            </Typography>
            {professionalDisplay && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Profissional: {professionalDisplay}
              </Typography>
            )}
            {guest && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Teleconsultoria: {guest.practitioner.display}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      {step === "teleconsultoria" && (
        <DialogActions>
          <Button
            onClick={() => setStep("patient")}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Voltar
          </Button>
          <Button
            onClick={() => {
              setGuest(null);
              setStep("confirm");
            }}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
           Prosseguir sem selecionar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setStep("confirm")}
            disabled={loading || !guest}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Continuar
          </Button>
        </DialogActions>
      )}
      {step === "confirm" && (
        <DialogActions>
          <Button
            onClick={() =>
              setStep(contractTeleconsultoria ? "teleconsultoria" : "patient")
            }
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleConfirm()}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading ? "Iniciando..." : "Iniciar evolução"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
